import { ApolloLink, split } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { setContext } from 'apollo-link-context';
import { createHttpLink } from 'apollo-link-http';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';
import { InMemoryCache, defaultDataIdFromObject } from 'apollo-cache-inmemory';
import jwtDecode from 'jwt-decode';
import { lStorage } from 'modules/utils/LocalStorage/LocalStorageHelper';
import { cookiesHelper } from 'modules/utils/Cookies/CookiesHelper';
import { decodeLavuToken } from 'utils/jwtUtils';
import {apolloSubscriptionTransportClient} from "./ApolloSubscriptionTransportClient";
import { ApolloClient } from 'apollo-client';
import axios from 'axios';
import { hProtect } from 'Authentication/Protect';

import {
	SERVER_URL,
	GRAPHQL_URL,
	PHP_URL,
	AXIOS_PRE_AUTH_APIS,
} from './utils/constants';

// add axios interceptor
// Add a request interceptor
axios.interceptors.request.use(function (config) {

	const url = new URL(config.url);
	const apiOrigin = url.origin;
	const apiPath = url.pathname;

	// Do something before request is sent
	if (apiOrigin == SERVER_URL  && AXIOS_PRE_AUTH_APIS.includes(apiPath)) {
		const hToken = hProtect();
		if (hToken) {
			config.headers.Authorization = 'Bearer ' + hToken;
		}
	}
	return config;
}, function (error) {
	// Do something with request error
	return Promise.reject(error);
});

// The http link is a terminating link that fetches
// GraphQL results from a GraphQL endpoint over an http connection.
/* eslint new-cap: 0 */
const httpLink = new createHttpLink({
	uri: GRAPHQL_URL,
	credentials: 'include',
});

// WS Link for subscriptions
const wsLink = new WebSocketLink(
	apolloSubscriptionTransportClient
);

// The setContext function takes a function that
// returns either an object or a promise that returns an object to set the new context of a request.
// It receives two arguments: the GraphQL request being executed, and the previous context.
// This link will perform async look up of things like authentication tokens and more!
const authLink = setContext(async (_, { headers }) => {
	// get the authentication token from local storage if it exists
	const token = await cookiesHelper.get('LAVU_TOKEN');

    // Inject the xsrf header, if we have it
    const xsrf = await cookiesHelper.get('XSRF-TOKEN');

	// return the headers to the context so httpLink can read them
	return {
		headers: {
			...headers,
			authorization: token ? `Bearer ${token}` : '',
            'X-XSRF-TOKEN' : xsrf ? xsrf : ''
		},
	};
});

export const resetStoreAndLogout = () => {
	// redirect to the login and clear everything...
	client.clearStore();
	client.cache.reset();
	const switchCP4LastSeen = localStorage.getItem(
		'SWITCH_CP4_MODAL_LAST_DISMISSED'
	);

	localStorage.clear();
	cookiesHelper.remove('CP3_ENABLED');
	cookiesHelper.remove('LAVU_TOKEN');
	lStorage.set({ key: '@session-logout', value: true });

	// Persist data about users who have dismissed the "Switch to CP4" modal
	if (switchCP4LastSeen) {
		localStorage.setItem('SWITCH_CP4_MODAL_LAST_DISMISSED', switchCP4LastSeen);
	}

	window.location.href = '/login';
};

// When using Apollo Link, the ability to handle network errors is way more powerful.
// The best way to do this is to use the apollo-link-error to catch and handle server errors,
// network errors, and GraphQL errors.
const errorLink = onError(({ networkError, graphQLErrors }) => {
	const result =
		networkError && networkError.hasOwnProperty('result')
			? networkError.result
			: null;

	if (
		result &&
		result.hasOwnProperty('isNewControlPanelActive') &&
		result.isNewControlPanelActive === 0 &&
		networkError.statusCode === 403
	) {
		cookiesHelper.remove('CP3_ENABLED');
		// eslint-disable-next-line no-return-assign
		return (window.location.href = PHP_URL);
	}

	if (graphQLErrors) {
		graphQLErrors.map(({ message, locations, path }) => {
			// eslint-disable-next-line no-console
			console.log(
				`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
					locations
				)}, Path: ${path}`
			);
			return message;
		});
	}

	if (result && result.hasOwnProperty('token')) {
		cookiesHelper.set('LAVU_TOKEN', result.token, 30, '/');
		const user = decodeLavuToken();

		lStorage.set({
			key: 'current-user',
			value: { user },
		});

		// Reload the page to get the refreshed token from HubQL
		window.location.reload();

		return null;
	}

	const lavuToken = cookiesHelper.get('LAVU_TOKEN') || '';
	const jwtTokenDecoded = lavuToken ? jwtDecode(lavuToken) : {};
	const exp = jwtTokenDecoded.exp || 0;
	const nowInSeconds = Math.round(Date.now() / 1000);

	if (nowInSeconds >= exp) {
		return resetStoreAndLogout();
	}

	return null;
});

// using the ability to split links, you can send data to each link
// depending on what kind of operation is being sent
const terminalLink = split(
	// split based on operation type
	({ query }) => {
		const definition = getMainDefinition(query);
		return (
			definition.kind === 'OperationDefinition' &&
			definition.operation === 'subscription'
		);
	},
	wsLink,
	httpLink
);

const link = ApolloLink.from([errorLink, authLink, terminalLink]);

// The custom function passed to `dataIdFromObject` allows us
// to return a unique value for the different objects that
// come from the server. Because we're including the __typename
// each ID in the Apollo cache will have it and ensure unique
// normalization. Without __typename, the ID alone wouldn't
// be enough as it will conflict when multiple queries
// return the same ID
const cache = new InMemoryCache({
	dataIdFromObject: object => defaultDataIdFromObject(object),
});


// The ApolloClient is what is going to communicate directly with the
// GraphQL server and store data that comes back from it. So, it is
// essentially a client-side repository of all the data that is coming
// back from our server.
export const client = new ApolloClient({
	link,
	cache,
	// Check https://www.apollographql.com/docs/react/data/local-state/#handling-client-fields-with-resolvers
	// we can resolvers here for now and as they grow we can move them separate files/folders
	resolvers: {
		Restaurant: {
			fullRestName: rest => `${rest.locationName} (${rest.dataname})`,
		},
	},
});